import { useMemo, useCallback, useEffect } from 'react'
import { format } from 'date-fns'
import { convertUtcToEst } from 'utils/DateAndTimeFormat'
import { useAppSelector } from '../../../redux/store'
import { APIStatus } from '../../../redux/types'

export const useLobby = () => {
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const queueStatus = useAppSelector((state: any) => state.lobbyPortal.queueStatus)
  const priorityVisit = useAppSelector((state) => state.lobbyPortal.priorityVisit)

  // your EST timestamp formatter
  const timeStamp = useCallback((time: string) => {
    const stamp = new Date(time)
    const estTime = convertUtcToEst(stamp)
    return format(estTime, 'h:mm a')
  }, [])

  const result = useMemo(() => {
    // Only process when data is fully loaded

    if (!queue || !Array.isArray(queue.checkedIn) || queueStatus !== APIStatus.fulfilled) {
      return {
        checkedIn: { ontime: [], late: [] },
        upcoming: [],
        previous: [],
      }
    }

    const threshold = 10 * 60 * 1000 // 10 mins

    // 1. Split CHECKED-IN → ON-TIME & LATE

    const ontime: any[] = []
    const late: any[] = []

    const checkedInList = Array.isArray(queue?.checkedIn) ? queue.checkedIn : []

    checkedInList.forEach((item: any) => {
      const check = item.checkinTime ? new Date(item.checkinTime).getTime() : 0
      const appt = item.visit?.appointmentTime ? new Date(item.visit.appointmentTime).getTime() : 0
      const isLate = check > appt + threshold

      if (isLate) late.push(item)
      else ontime.push(item)
    })

    // 2. Timestamp grouping

    const addTimestampFlag = (list: any[], field = 'appointmentTime') => {
      return list.map((item, index) => {
        const fieldValue = field === 'appointmentTime' ? item.visit?.appointmentTime : item[field]
        const current = fieldValue ? timeStamp(fieldValue) : '-'
        const prevFieldValue =
          index > 0 && list[index - 1]
            ? field === 'appointmentTime'
              ? list[index - 1].visit?.appointmentTime
              : list[index - 1][field]
            : null
        const previous = prevFieldValue ? timeStamp(prevFieldValue) : null

        return {
          ...item,
          showTimeStamp: current !== previous,
          timeStamp: current,
        }
      })
    }

    // Checked-in: add timestamp to ON-TIME only
    const checkedIn = {
      ontime: addTimestampFlag(ontime),
      late,
    }

    // Upcoming: add timestamps
    const upcoming = addTimestampFlag(queue.upcoming)

    // Previous: no timestamp
    const previous = queue.previous

    return { checkedIn, upcoming, previous }
  }, [queueStatus, queue, timeStamp])

  return result
}
